"use client";

import { useState } from "react";
import { Checkbox } from "@/components/tool-kit/primitives";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";

const LETTER_VALUES: Record<string, number> = {
  a: 1,
  b: 3,
  c: 3,
  d: 2,
  e: 1,
  f: 4,
  g: 2,
  h: 4,
  i: 1,
  j: 8,
  k: 5,
  l: 1,
  m: 3,
  n: 1,
  o: 1,
  p: 3,
  q: 10,
  r: 1,
  s: 1,
  t: 1,
  u: 1,
  v: 4,
  w: 4,
  x: 8,
  y: 4,
  z: 10,
};

const LETTERS_ONLY_REGEX = /^[a-zA-Z]*$/;

type LetterMultiplier = 1 | 2 | 3;
type WordMultiplier = 1 | 2 | 3;

const LETTER_MULTIPLIER_VALUES: LetterMultiplier[] = [1, 2, 3];
const WORD_MULTIPLIER_VALUES: WordMultiplier[] = [1, 2, 3];

/** English strings. A translated content file overrides these through `ui`. */
export const SCRABBLE_SCORE_CALCULATOR_STRINGS = {
  letterMultiplierLabels: {
    "1": "—",
    "2": "DL",
    "3": "TL",
  },
  wordMultiplierLabels: {
    "1": "No word multiplier",
    "2": "Double word (DW)",
    "3": "Triple word (TW)",
  },
  yourWord: "Your word",
  wordPlaceholder: "QUIZ",
  lettersOnlyError: "Letters only — no numbers, spaces, or punctuation.",
  tilesLabel: "Tiles — set DL/TL under any letter on a premium square",
  multiplierForLetter: "Multiplier for letter {position} ({letter})",
  wordMultiplierLabel: "Word multiplier",
  totalScore: "Total score",
  baseScore: "Base score (no multipliers)",
  letters: "Letters",
  bingoLabel:
    "All seven tiles came from my rack (adds the 50-point bingo bonus)",
  totalWithBingo: "Total with bingo bonus",
  clear: "Clear",
} as const;

/** Playing all seven rack tiles in one turn. The bonus is flat: no multiplier touches it. */
export const BINGO_BONUS = 50;
const BINGO_MIN_LETTERS = 7;

/** A bingo needs seven rack tiles, so it is only possible in a word of seven or more letters. */
export function canBingo(wordLength: number): boolean {
  return wordLength >= BINGO_MIN_LETTERS;
}

function letterValue(letter: string): number {
  return LETTER_VALUES[letter.toLowerCase()] ?? 0;
}

export function computeTotal(
  word: string,
  letterMultipliers: LetterMultiplier[],
  wordMultiplier: WordMultiplier
): number {
  let sum = 0;
  for (let i = 0; i < word.length; i += 1) {
    sum += letterValue(word[i]) * (letterMultipliers[i] ?? 1);
  }
  return sum * wordMultiplier;
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

export default function ScrabbleScoreCalculatorWidget() {
  const strings = useToolUi(SCRABBLE_SCORE_CALCULATOR_STRINGS);
  const [word, setWord] = useState("");
  const [error, setError] = useState("");
  const [letterMultipliers, setLetterMultipliers] = useState<
    LetterMultiplier[]
  >([]);
  const [wordMultiplier, setWordMultiplier] = useState<WordMultiplier>(1);
  const [bingo, setBingo] = useState(false);

  const letters = word.split("");
  const baseScore = letters.reduce((sum, l) => sum + letterValue(l), 0);
  const total = computeTotal(word, letterMultipliers, wordMultiplier);

  function handleWordChange(value: string) {
    if (!LETTERS_ONLY_REGEX.test(value)) {
      setError(strings.lettersOnlyError);
      return;
    }
    setError("");
    setWord(value);
    setLetterMultipliers(new Array(value.length).fill(1));
    if (!canBingo(value.length)) {
      setBingo(false);
    }
  }

  function handleLetterMultiplierChange(index: number, value: string) {
    const next = [...letterMultipliers];
    next[index] = Number(value) as LetterMultiplier;
    setLetterMultipliers(next);
  }

  function handleClear() {
    setWord("");
    setError("");
    setLetterMultipliers([]);
    setWordMultiplier(1);
    setBingo(false);
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="scrabble-word"
        >
          {strings.yourWord}
        </label>
        <input
          autoCapitalize="off"
          autoComplete="off"
          className="w-full rounded-lg border border-border bg-card p-4 font-mono text-foreground text-lg tracking-widest placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="scrabble-word"
          maxLength={15}
          onChange={(e) => handleWordChange(e.target.value)}
          placeholder={strings.wordPlaceholder}
          spellCheck={false}
          type="text"
          value={word}
        />
        {error.length > 0 && (
          <p className="text-red-600 text-xs dark:text-red-400">{error}</p>
        )}
      </div>

      {/* Tiles */}
      {word.length > 0 && (
        <section className="space-y-3">
          <p className="font-medium text-foreground text-sm">
            {strings.tilesLabel}
          </p>
          <div className="flex flex-wrap gap-3">
            {letters.map((letter, index) => (
              <div
                className="flex flex-col items-center gap-2"
                // biome-ignore lint/suspicious/noArrayIndexKey: letters can repeat; position is the identity
                key={`${letter}-${index}`}
              >
                <div className="relative flex h-14 w-14 items-center justify-center rounded-md border border-amber-300 bg-amber-100 font-bold text-2xl text-amber-900 shadow-sm dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-100">
                  {letter.toUpperCase()}
                  <span className="absolute right-1 bottom-0.5 font-semibold text-xs">
                    {letterValue(letter)}
                  </span>
                </div>
                <select
                  aria-label={t(strings.multiplierForLetter, {
                    position: index + 1,
                    letter: letter.toUpperCase(),
                  })}
                  className="rounded-md border border-border bg-card px-1.5 py-1 text-foreground text-xs focus:outline-none focus:ring-2 focus:ring-primary"
                  onChange={(e) =>
                    handleLetterMultiplierChange(index, e.target.value)
                  }
                  value={letterMultipliers[index] ?? 1}
                >
                  {LETTER_MULTIPLIER_VALUES.map((v) => (
                    <option key={v} value={v}>
                      {
                        strings.letterMultiplierLabels[
                          String(v) as "1" | "2" | "3"
                        ]
                      }
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Word multiplier */}
      {word.length > 0 && (
        <section className="space-y-3">
          <p className="font-medium text-foreground text-sm">
            {strings.wordMultiplierLabel}
          </p>
          <div className="flex flex-wrap gap-3">
            {WORD_MULTIPLIER_VALUES.map((v) => (
              <label
                className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                key={v}
              >
                <input
                  checked={wordMultiplier === v}
                  className="accent-primary"
                  name="word-multiplier"
                  onChange={() => setWordMultiplier(v)}
                  type="radio"
                  value={v}
                />
                {strings.wordMultiplierLabels[String(v) as "1" | "2" | "3"]}
              </label>
            ))}
          </div>
        </section>
      )}

      {/* Stats */}
      {word.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.totalScore} value={total} />
          <StatCard label={strings.baseScore} value={baseScore} />
          <StatCard label={strings.letters} value={word.length} />
          {bingo && canBingo(word.length) && (
            <StatCard
              label={strings.totalWithBingo}
              value={total + BINGO_BONUS}
            />
          )}
        </div>
      )}

      {canBingo(word.length) && (
        <Checkbox
          checked={bingo}
          label={strings.bingoLabel}
          onChange={setBingo}
        />
      )}

      {/* Clear */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={word.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
    </>
  );
}
